import { HttpContextToken } from '@angular/common/http';
import { CollectionRevisionAuthInteractionKind } from '../interfaces/collection-revision-connection.interface';

export type CommitTargetFileSetIdempotentOperation = {
  kind: 'commit-target-file-set';
  collectionId: string;
  revisionId: string;
  targetId: string;
  targetEtag: string;
  fileInputs: readonly {
    readonly file_id: string;
    readonly resolved_inputs: Readonly<Record<string, string>>;
  }[];
};

interface ExactConnectionIdempotentOperationBase {
  readonly collectionId: string;
  readonly revisionId: string;
  readonly targetId: string;
  readonly connectionId: string;
}

export interface ConnectIdempotentOperation
  extends ExactConnectionIdempotentOperationBase {
  readonly kind: 'connect';
  readonly entityId: string;
}

export interface UpdateCredentialsIdempotentOperation
  extends ExactConnectionIdempotentOperationBase {
  readonly kind: 'update-credentials';
}

interface AuthInteractionIdempotentOperationBase
  extends ExactConnectionIdempotentOperationBase {
  readonly challengeId: string;
  readonly generation: number;
  readonly authInteractionKind: CollectionRevisionAuthInteractionKind;
}

export interface SubmitExactAuthInteractionIdempotentOperation
  extends AuthInteractionIdempotentOperationBase {
  readonly kind: 'submit-auth-interaction';
}

export interface ResendExactAuthInteractionIdempotentOperation
  extends AuthInteractionIdempotentOperationBase {
  readonly kind: 'resend-auth-interaction';
}

interface ExactTargetEntityIdempotentOperationBase {
  readonly collectionId: string;
  readonly revisionId: string;
  readonly targetId: string;
  readonly entityId: string;
}

export interface GenerateWaiverBatchIdempotentOperation
  extends ExactTargetEntityIdempotentOperationBase {
  readonly kind: 'waiver-batch-generate';
  readonly waiverType: 'INDIVIDUAL' | 'BUSINESS' | 'REPRESENTATIVE';
}

export interface SignWaiverBatchIdempotentOperation
  extends ExactTargetEntityIdempotentOperationBase {
  readonly kind: 'waiver-batch-sign';
  readonly waiverBatchId: string;
}

export interface CancelConnectionIdempotentOperation
  extends ExactConnectionIdempotentOperationBase {
  readonly kind: 'cancel-connection';
}

export type CollectionIdempotentOperation =
  | CommitTargetFileSetIdempotentOperation
  | ConnectIdempotentOperation
  | UpdateCredentialsIdempotentOperation
  | SubmitExactAuthInteractionIdempotentOperation
  | ResendExactAuthInteractionIdempotentOperation
  | GenerateWaiverBatchIdempotentOperation
  | SignWaiverBatchIdempotentOperation
  | CancelConnectionIdempotentOperation;

export const COLLECTION_IDEMPOTENCY_OPERATION =
  new HttpContextToken<CollectionIdempotentOperation | null>(() => null);

export function getCollectionIdempotentOperationIdentity(
  operation: CollectionIdempotentOperation,
): string {
  switch (operation.kind) {
    case 'submit-auth-interaction':
      return exactConnectionIdentity(
        operation,
        operation.challengeId,
        String(operation.generation),
        operation.authInteractionKind,
      );

    case 'commit-target-file-set':
      return [
        operation.kind,
        operation.collectionId,
        operation.revisionId,
        operation.targetId,
        operation.targetEtag,
        JSON.stringify(operation.fileInputs),
      ].join(':');

    case 'connect':
      return exactConnectionIdentity(operation, operation.entityId);

    case 'update-credentials':
    case 'cancel-connection':
      return exactConnectionIdentity(operation);

    case 'resend-auth-interaction':
      return exactConnectionIdentity(
        operation,
        operation.challengeId,
        String(operation.generation),
        operation.authInteractionKind,
      );

    case 'waiver-batch-generate':
      return exactTargetEntityIdentity(operation, operation.waiverType);

    case 'waiver-batch-sign':
      return exactTargetEntityIdentity(operation, operation.waiverBatchId);
  }
}

function exactTargetEntityIdentity(
  operation: ExactTargetEntityIdempotentOperationBase & {
    readonly kind: string;
  },
  ...qualifiers: readonly string[]
): string {
  return [
    operation.kind,
    operation.collectionId,
    operation.revisionId,
    operation.targetId,
    operation.entityId,
    ...qualifiers,
  ].join(':');
}

function exactConnectionIdentity(
  operation: ExactConnectionIdempotentOperationBase & { readonly kind: string },
  ...qualifiers: readonly string[]
): string {
  return [
    operation.kind,
    operation.collectionId,
    operation.revisionId,
    operation.targetId,
    operation.connectionId,
    ...qualifiers,
  ].join(':');
}
